import { useCallback, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ExternalLink, FolderOpen } from 'lucide-react';
import { FSRef, TypeId, dataContext } from '@sdk';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@src/components/ui/hover-card';
import { useCloseOnIframeFocus } from '@src/hooks/use-close-on-iframe-focus';
import { DockPointer } from '@src/navigation/DockPointer';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { CopyPathButton } from './CopyPathButton';

/**
 * "What exactly am I looking at" — the details behind the last breadcrumb.
 *
 * This is where the asset editor's header row went. That row showed the file's
 * real name, its parent directory, a copy-path button and a reveal-in-Finder
 * glyph, all of which duplicated the bar sitting directly above it. Only the
 * PATH was genuinely unique, and a path belongs behind the address, not beside
 * it — which is where a browser keeps it too.
 *
 * Rendered only for a file- or folder-backed dock. A conversation, a process or
 * a list has no path, and the crumb stays plain text.
 *
 * Opens on HOVER, like the project crumb's card beside it — the two are the
 * same kind of affordance and must not need different gestures. A click opens
 * it too, for touch and for anyone who reaches for it that way.
 *
 * Radix dismisses a click-opened card on an outside pointer-down and on Escape
 * by itself. What it cannot see is a click inside an EMBEDDED FRAME — the html
 * preview, a webapp display, the deck viewer — because those events never reach
 * this document, and the card then hangs over the page the user just clicked
 * into. Focus is the one signal that crosses that boundary, so it closes on it.
 */
export function CrumbDetailsPopover({
  label,
  filename,
  path,
  directory = false,
  children,
}: {
  label: string;
  /** Real basename, with extension — `label` is the display name, which drops it. */
  filename: string | null | undefined;
  path: string;
  /** The path IS a folder (a Files dock). "Open in Files" then opens it rather
   *  than its parent, and the OS reveal opens it rather than selecting it. */
  directory?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useLingui();
  const { navigation } = useDockNavigation();
  const [open, setOpen] = useState(false);
  /** Whether the card was open when the trigger was pressed — read on the click
   *  that follows, because Radix's own outside-dismiss (the trigger sits
   *  outside the card) has already flipped `open` to false by then, and
   *  toggling THAT would re-open the card the press meant to close. */
  const openAtPointerDown = useRef(false);
  /** Set when the card is dismissed, cleared by the next real hover. Radix arms
   *  its hover-open timer on pointerenter and does not cancel it when the card
   *  is opened by a click and then dismissed — so a hover-then-click-then-Escape
   *  inside `openDelay` had the stale timer re-open the card ~100ms later, with
   *  no new pointer or focus event. Only a fresh pointerenter may open it again. */
  const hoverOpenSuppressed = useRef(false);
  const onOpenChange = useCallback((next: boolean) => {
    if (next && hoverOpenSuppressed.current) return;
    if (!next) hoverOpenSuppressed.current = true;
    setOpen(next);
  }, []);

  const close = useCallback(() => setOpen(false), []);
  useCloseOnIframeFocus(open, close);

  const parentDir = path.replace(/[\\/]+$/, '').replace(/[\\/][^\\/]+$/, '') || path;
  const filesTarget = directory ? path : parentDir;

  const openInFiles = useCallback(() => {
    setOpen(false);
    navigation.openDock(DockPointer.forExplorer(filesTarget));
  }, [navigation, filesTarget]);

  // Reveal through the compute node the path actually belongs to, so it keeps
  // working for an asset on a remote node; `localComputeNodeId` is null there,
  // which is what hides the button.
  const fsRef = new FSRef(path, new TypeId('compute_node', '@local'));
  // A file-manager window only reaches a person on a desktop — not on the hub (no @local),
  // not on a cloud box (no screen).
  const canReveal = dataContext.caps.osDialogs && !!fsRef.localComputeNodeId;

  return (
    <HoverCard open={open} onOpenChange={onOpenChange} openDelay={200} closeDelay={100}>
      <HoverCardTrigger
        asChild
        onPointerEnter={() => {
          hoverOpenSuppressed.current = false;
        }}
        onPointerDownCapture={() => {
          openAtPointerDown.current = open;
        }}
        onClick={() => setOpen(!openAtPointerDown.current)}
      >
        {children}
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" className="w-96 p-3" data-testid="top-nav-crumb-details">
        <div className="truncate text-sm font-medium text-foreground" title={filename || label}>
          {filename || label}
        </div>

        <CopyPathButton path={path} testId="top-nav-crumb-copy-path" className="mt-1.5 w-full" />

        <div className="mt-2 flex items-center gap-1">
          <button
            type="button"
            onClick={openInFiles}
            data-testid="top-nav-crumb-open-files"
            className="flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <FolderOpen className="h-3.5 w-3.5" />
            {t`Open in Files`}
          </button>
          {canReveal && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void fsRef.open(directory ? undefined : { select: true });
              }}
              data-testid="top-nav-crumb-reveal"
              className="flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t`Reveal in Finder`}
            </button>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
